import React from 'react';
import { StarIcon, RocketLaunchIcon, UserGroupIcon, LightBulbIcon } from '@heroicons/react/24/outline';

const HackathonPrizes: React.FC = () => {
  const prizes = [
    {
      title: "🌟 Ultimate Champion",
      amount: "$1,000",
      value: "$7,000+",
      description: "Build the most groundbreaking project and take home the grand prize!",
      perks: [
        "$1,000 cash prize",
        "$1,000 in Preswald credits",
        "1:1 Mentorship with industry leaders",
        "Featured on our platform"
      ],
      icon: StarIcon,
      color: 'text-cyan-500',
      borderColor: 'border-cyan-500',
      hoverBg: 'hover:bg-cyan-50'
    },
    {
      title: "✨ Rising Star",
      amount: "$500",
      value: "$3,500+",
      description: "Show off your technical brilliance and win big!",
      perks: [
        "$500 cash prize",
        "$500 in Preswald credits",
        "Direct industry connections",
        "Project showcase to investors"
      ],
      icon: StarIcon,
      color: 'text-yellow-500',
      borderColor: 'border-yellow-500',
      hoverBg: 'hover:bg-yellow-50'
    },
    {
      title: "💫 Trailblazer",
      amount: "$250",
      value: "$2,000+",
      description: "Create something unique and get rewarded for your innovation!",
      perks: [
        "$250 cash prize",
        "$250 in Preswald credits",
        "Premium development resources",
        "Community spotlight"
      ],
      icon: RocketLaunchIcon,
      color: 'text-purple-500',
      borderColor: 'border-purple-500',
      hoverBg: 'hover:bg-purple-50'
    }
  ];

  const additionalRewards = [
    {
      title: "People's Choice",
      icon: UserGroupIcon,
      description: "Let the community crown your project as their favorite!",
      value: "$500 in Preswald credits",
      color: 'bg-cyan-500'
    },
    {
      title: "First Flight",
      icon: RocketLaunchIcon,
      description: "Special recognition and prizes for first-time hackers",
      value: "$300 in Preswald credits",
      color: 'bg-yellow-500'
    },
    {
      title: "Game Changer",
      icon: LightBulbIcon,
      description: "Transform your idea into a revolutionary solution",
      value: "$200 in Preswald credits",
      color: 'bg-purple-500'
    }
  ];

  return (
    <div className="relative max-w-6xl mx-auto px-4 py-16">
      {/* Main Prizes Section - With background blur */}
      <div className="relative z-10 mb-24">
        <div className="absolute inset-0 bg-white/60 backdrop-blur-sm rounded-3xl shadow-[0_0_30px_rgba(0,0,0,0.1)]" />
        <div className="relative z-10 px-8 py-16">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Win Big, Build Bigger!
          </h2>
          <p className="text-xl text-slate-600 text-center mb-12 max-w-3xl mx-auto">
            Turn your ideas into reality and compete for over <span className="font-bold text-cyan-600">$12,500</span> in total prizes, including cash rewards, Preswald credits, mentorship, and exclusive opportunities to launch your project to the next level.
          </p>
          
          <div className="flex flex-col md:flex-row justify-center items-stretch gap-8">
            {prizes.map((prize, index) => (
              <div
                key={index}
                className="w-full md:w-[350px] group"
              >
                <div className={`flex flex-col items-center bg-white/90 backdrop-blur-sm p-6 border-2 ${prize.borderColor} ${prize.hoverBg}
                  transition-all duration-300 ease-in-out
                  hover:shadow-lg hover:-translate-y-2 hover:rotate-2
                  group-hover:border-opacity-100 border-opacity-50 h-full rounded-xl`}>
                  {/* Icon */}
                  <div className={`text-4xl mb-4 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12 ${prize.color}`}>
                    <prize.icon className="w-12 h-12" />
                  </div>
                  
                  {/* Prize Title and Amount */}
                  <div className="text-2xl font-bold mb-2">{prize.title}</div>
                  <div className="flex flex-col items-center mb-4">
                    <div className={`text-4xl font-bold ${prize.color}`}>
                      {prize.amount}
                    </div>
                    <div className="text-sm text-slate-500">
                      Plus Preswald credits & exclusive perks
                    </div>
                  </div>
                  
                  {/* Description */}
                  <p className="text-slate-600 mb-6 text-center">
                    {prize.description}
                  </p>
                  
                  {/* Perks List */}
                  <ul className="space-y-3 w-full">
                    {prize.perks.map((perk, perkIndex) => (
                      <li key={perkIndex} className="flex items-center text-slate-700">
                        <svg
                          className={`h-5 w-5 ${prize.color} mr-2`}
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path d="M5 13l4 4L19 7"></path>
                        </svg>
                        {perk}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* More Ways to Win Section - With background */}
      <div className="relative max-w-4xl mx-auto">
        <div className="absolute inset-0 bg-white rounded-3xl shadow-lg" />
        <div className="relative z-10 px-8 py-16">
          <div className="flex flex-col items-center mb-8">
            <div className="text-6xl mb-4">🏆</div>
            <h3 className="text-2xl font-bold text-center mb-4">More Ways to Win!</h3>
            <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
              Beyond the main prizes, there are multiple opportunities to showcase your talent and get recognized. Each category winner receives additional prizes and opportunities!
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {additionalRewards.map((reward, index) => (
              <div 
                key={index} 
                className="group relative"
              >
                <div className="relative bg-white rounded-2xl p-6 h-full
                  transition-all duration-300 ease-in-out
                  hover:shadow-lg hover:-translate-y-1
                  border border-slate-200">
                  <div className="flex flex-col items-center text-center">
                    <div className={`p-3 rounded-xl ${reward.color} mb-4`}>
                      <reward.icon className="w-8 h-8 text-white" />
                    </div>
                    <h4 className="text-xl font-semibold mb-2">{reward.title}</h4>
                    <p className="text-slate-600 mb-4">{reward.description}</p>
                    <div className="mt-auto pt-4">
                      <div className="inline-block px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition-colors">
                        {reward.value}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HackathonPrizes; 